import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';

/* Loops public form endpoint — safe to call from the browser.
   Set REACT_APP_LOOPS_FORM_ID in Amplify to the single form UUID from
   Loops → Forms. Both EN and ES UIs post to the same endpoint; the
   hidden `lang` field lets you segment signups inside Loops. */
const LOOPS_FORM_ID = process.env.REACT_APP_LOOPS_FORM_ID_EN;

const GUIDES = {
  en: {
    path: '/guides/nekochan-japanese-from-zero-en.pdf',
    filename: 'Nekochan-Japanese-From-Zero.pdf',
  },
  es: {
    path: '/guides/nekochan-japones-desde-cero-es.pdf',
    filename: 'Nekochan-Japones-Desde-Cero.pdf',
  },
};

function langKey(lang) {
  return lang?.startsWith('es') ? 'es' : 'en';
}

function DownloadPanel() {
  const { t, i18n } = useTranslation();
  const guide = GUIDES[langKey(i18n.language)];
  return (
    <div className="rounded-2xl border border-brand/30 bg-brand/5 p-8 text-center">
      <span className="text-4xl" role="img" aria-hidden="true">🎉</span>
      <h3 className="font-rubik font-bold text-h4 text-gray-900 mt-3">
        {t('freeGuide.form.successTitle')}
      </h3>
      <p className="font-rubik text-body text-gray-600 mt-2 mb-6">
        {t('freeGuide.form.successBody')}
      </p>
      <a
        href={guide.path}
        download={guide.filename}
        className="inline-block rounded-xl bg-brand hover:bg-[#0fa8a8] text-white font-rubik font-semibold text-body px-6 py-3 transition-colors"
      >
        {t('freeGuide.form.downloadCta')}
      </a>
    </div>
  );
}

function GuideForm({ onSuccess }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState('idle');

  const lang = langKey(i18n.language);

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus('sending');

    const data = new FormData(event.target);
    /* Loops honeypot: real users leave it empty */
    if (data.get('website')) {
      setStatus('sent');
      onSuccess();
      return;
    }

    const body = new URLSearchParams();
    body.append('email', data.get('email'));
    body.append('userGroup', `free-guide-${lang}`);
    body.append('source', `nekochan.app/free-guide/${lang}`);

    try {
      const res = await fetch(`https://app.loops.so/api/newsletter-form/${LOOPS_FORM_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('sent');
        onSuccess();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* honeypot */}
      <input
        type="text"
        name="website"
        tabIndex="-1"
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      <div>
        <label htmlFor="guide-email" className="block font-rubik font-medium text-bodyS text-gray-800 mb-1">
          {t('freeGuide.form.emailLabel')}
        </label>
        <input
          id="guide-email"
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder={t('freeGuide.form.emailPlaceholder')}
          className="w-full rounded-xl border-gray-300 font-rubik text-body focus:border-brand focus:ring-brand"
        />
      </div>

      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full rounded-xl bg-brand hover:bg-[#0fa8a8] disabled:opacity-60 text-white font-rubik font-semibold text-body py-3 transition-colors"
      >
        {status === 'sending' ? t('freeGuide.form.sending') : t('freeGuide.form.submit')}
      </button>

      <p className="font-rubik text-small text-gray-500 text-center">
        {t('freeGuide.form.privacyNote')}
      </p>

      {status === 'error' && (
        <p className="font-rubik text-bodyS text-red text-center">
          {t('freeGuide.form.error')}
        </p>
      )}
    </form>
  );
}

export default function FreeGuide() {
  const { t } = useTranslation();
  const [unlocked, setUnlocked] = useState(false);

  const perks = t('freeGuide.perks', { returnObjects: true });
  const hasFormId = Boolean(LOOPS_FORM_ID);

  return (
    <div className="bg-white">
      <Meta titleKey="freeGuide.meta.title" descKey="freeGuide.meta.description" />

      <div className="max-w-3xl mx-auto px-4 py-12 md:py-16">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center"
        >
          <span className="inline-block rounded-full bg-brand/15 text-[#0a8f8f] font-rubik font-semibold text-small px-3 py-1 mb-4">
            {t('freeGuide.badge')}
          </span>
          <h1 className="font-rubik font-bold text-h2 md:text-h1 text-gray-900 mb-4">
            {t('freeGuide.heading')}
          </h1>
          <p className="font-rubik text-body text-gray-600">
            {t('freeGuide.intro')}
          </p>
        </motion.header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="rounded-2xl border border-gray-200 p-6 md:p-8">
            <h2 className="font-rubik font-bold text-h4 text-gray-900 mb-4">
              {t('freeGuide.perksHeading')}
            </h2>
            <ul className="space-y-3">
              {perks.map((perk) => (
                <li key={perk} className="flex gap-2 font-rubik text-body text-gray-700">
                  <span className="text-brand" aria-hidden="true">✓</span>
                  {perk}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 p-6 md:p-8">
            {unlocked || !hasFormId ? (
              <DownloadPanel />
            ) : (
              <GuideForm onSuccess={() => setUnlocked(true)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
